import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { dataService } from '../services/dataService';
import { confirmDialog } from '../utils/dialog';
import { ROLE_PRESETS, getRoleLabel } from '../constants/roles';
import { useWorkspaceRole } from '../contexts/WorkspaceRoleContext';

const callGenerateKey = httpsCallable(functions, 'generateApiKey');
const callRevokeKey   = httpsCallable(functions, 'revokeApiKey');

const EXPIRY_OPTIONS = [
  { label: '7 days',   days: 7 },
  { label: '30 days',  days: 30 },
  { label: '90 days',  days: 90 },
  { label: '1 year',   days: 365 },
  { label: 'No expiry', days: null },
];

const ASSIGNABLE_ROLES = Object.entries(ROLE_PRESETS)
  .filter(([k]) => k !== 'owner')
  .map(([k, v]) => ({ value: k, label: v.label, description: v.description }));

function tsToDate(ts) {
  if (!ts) return null;
  return ts.toDate ? ts.toDate() : new Date(ts);
}

function formatDate(ts) {
  const d = tsToDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function keyState(k) {
  if (k.revoked) return { label: 'Revoked', cls: 'ws-key-revoked' };
  const exp = tsToDate(k.expiresAt);
  if (exp && exp.getTime() < Date.now()) return { label: 'Expired', cls: 'ws-key-expired' };
  return { label: 'Active', cls: 'ws-key-active' };
}

export default function WorkspaceSettingsView({ workspace, user, onWorkspaceUpdated }) {
  const workspaceId = workspace?.id;
  const { role: myRole, hasCapability } = useWorkspaceRole();
  const canManageMembers = hasCapability('manage_members');
  const canManageKeys = hasCapability('manage_api_keys');

  // ── Workspace details ──
  const [name, setName] = useState(workspace?.name || '');
  const [description, setDescription] = useState(workspace?.description || '');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);

  // ── Members ──
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  // ── API keys ──
  const [keys, setKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState(EXPIRY_OPTIONS[1]);
  const [generating, setGenerating] = useState(false);
  const [revealKey, setRevealKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(workspace?.name || '');
    setDescription(workspace?.description || '');
  }, [workspace?.id, workspace?.name, workspace?.description]);

  useEffect(() => {
    if (!workspaceId) { setLoadingKeys(false); return; }
    setLoadingKeys(true);
    const unsub = dataService.subscribeApiKeys(workspaceId, (items) => {
      setKeys(items);
      setLoadingKeys(false);
    });
    return () => unsub && unsub();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) { setLoadingMembers(false); return; }
    setLoadingMembers(true);
    dataService.getWorkspaceMembers(workspaceId).then(m => {
      setMembers(m);
      setLoadingMembers(false);
    }).catch(() => setLoadingMembers(false));
  }, [workspaceId]);

  const refreshMembers = async () => {
    if (!workspaceId) return;
    const m = await dataService.getWorkspaceMembers(workspaceId);
    setMembers(m);
  };

  const handleSaveDetails = async () => {
    if (!workspaceId || !name.trim()) return;
    setSavingDetails(true);
    try {
      await dataService.updateWorkspace(workspaceId, { name: name.trim(), description: description.trim() });
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 2000);
      if (onWorkspaceUpdated) onWorkspaceUpdated();
    } catch (e) {
      setError(e.message || 'Could not save workspace details.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleInvite = async () => {
    if (!workspaceId || !inviteEmail.trim()) return;
    setInviting(true);
    setError('');
    try {
      await dataService.addMemberByEmail(workspaceId, inviteEmail.trim().toLowerCase(), inviteRole);
      setInviteEmail('');
      await refreshMembers();
    } catch (e) {
      setError(e.message || 'Could not invite member.');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    setError('');
    try {
      await dataService.setMemberRole(workspaceId, uid, newRole);
      await refreshMembers();
    } catch (e) {
      setError(e.message || 'Could not update role.');
    }
  };

  const handleRemoveMember = async (uid, displayName) => {
    if (!(await confirmDialog({ title: 'Remove member', message: `Remove ${displayName || uid} from this workspace?`, confirmText: 'Remove', danger: true }))) return;
    setError('');
    try {
      await dataService.removeMember(workspaceId, uid);
      await refreshMembers();
    } catch (e) {
      setError(e.message || 'Could not remove member.');
    }
  };

  const handleGenerate = async () => {
    if (!workspaceId) return;
    setGenerating(true);
    setError('');
    try {
      const res = await callGenerateKey({
        workspaceId,
        name: newKeyName.trim() || 'Untitled key',
        expiryDays: newKeyExpiry.days,
      });
      setRevealKey({ key: res.data.key, prefix: res.data.prefix });
      setNewKeyName('');
    } catch (e) {
      setError(e.message || 'Could not generate key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (k) => {
    if (!(await confirmDialog({ title: 'Revoke API key', message: `Revoke "${k.name}"? Any integration using it will stop working immediately.`, confirmText: 'Revoke', danger: true }))) return;
    setError('');
    try {
      await callRevokeKey({ workspaceId, keyId: k.id });
    } catch (e) {
      setError(e.message || 'Could not revoke key.');
    }
  };

  const handleCopy = () => {
    if (!revealKey) return;
    navigator.clipboard.writeText(revealKey.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!workspace) {
    return <div className="ws-settings"><div className="ws-empty">No workspace selected.</div></div>;
  }

  return (
    <div className="ws-settings">
      <div className="ws-settings-header">
        <h2 className="ws-settings-title">Workspace Settings</h2>
        <span className="ws-settings-sub">{workspace.name}</span>
        {myRole && <span className="ws-role-badge ws-role-mine">{getRoleLabel(myRole)}</span>}
      </div>

      {error && <div className="ws-error">{error}</div>}

      {/* ── Details ── */}
      <section className="ws-card">
        <div className="ws-card-title">Details</div>
        <div className="ws-field">
          <label className="ws-label">Workspace name</label>
          <input className="ws-input" value={name} onChange={e => setName(e.target.value)} maxLength={80} />
        </div>
        <div className="ws-field">
          <label className="ws-label">Description</label>
          <textarea className="ws-textarea" value={description} onChange={e => setDescription(e.target.value)} rows={2} maxLength={240} />
        </div>
        <div className="ws-card-actions">
          <button className="ws-btn-primary" onClick={handleSaveDetails} disabled={savingDetails || !name.trim()}>
            {savingDetails ? 'Saving…' : detailsSaved ? 'Saved ✓' : 'Save changes'}
          </button>
        </div>
      </section>

      {/* ── Members ── */}
      <section className="ws-card">
        <div className="ws-card-title">Members</div>
        <p className="ws-card-desc">
          Manage who has access to this workspace and what they can do.
          Roles control which features each member can use.
        </p>

        {canManageMembers && (
          <div className="ws-invite-row">
            <input
              className="ws-input ws-invite-email"
              placeholder="Email address"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleInvite()}
            />
            <select
              className="ws-select"
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
            >
              {ASSIGNABLE_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <button className="ws-btn-primary" onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
              {inviting ? 'Inviting…' : 'Add member'}
            </button>
          </div>
        )}

        {loadingMembers ? (
          <div className="ws-keys-empty">Loading members…</div>
        ) : members.length === 0 ? (
          <div className="ws-keys-empty">No members found.</div>
        ) : (
          <div className="ws-members-table">
            <div className="ws-members-row ws-members-head">
              <span>User</span>
              <span>Role</span>
              <span />
            </div>
            {members.map(m => {
              const display = m.username || m.email || m.uid;
              const isMe = m.uid === user?.uid;
              const isOwner = m.role === 'owner';
              return (
                <div key={m.uid} className="ws-members-row">
                  <span className="ws-member-info">
                    <span className="ws-member-name">{display}{isMe ? ' (you)' : ''}</span>
                    {m.email && m.username && <span className="ws-member-email">{m.email}</span>}
                  </span>
                  <span>
                    {isOwner || !canManageMembers ? (
                      <span className={`ws-key-badge ${isOwner ? 'ws-key-active' : ''}`}>{getRoleLabel(m.role)}</span>
                    ) : (
                      <select
                        className="ws-select ws-role-select"
                        value={m.role}
                        onChange={e => handleRoleChange(m.uid, e.target.value)}
                      >
                        {ASSIGNABLE_ROLES.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    )}
                  </span>
                  <span className="ws-member-actions">
                    {canManageMembers && !isOwner && !isMe && (
                      <button className="ws-revoke-btn" onClick={() => handleRemoveMember(m.uid, display)}>Remove</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── API keys ── */}
      <section className="ws-card">
        <div className="ws-card-title">API Keys</div>
        <p className="ws-card-desc">
          Keys let external apps and websites read and write this workspace's <strong>data collections</strong> through
          the Projectly REST API. Writing to a collection runs any automations attached to it — exactly as if the
          change were made inside Projectly. Keys are shown once at creation; store them securely.
        </p>

        {canManageKeys && (
          <div className="ws-keygen">
            <input
              className="ws-input ws-keygen-name"
              placeholder="Key name (e.g. Marketing site)"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              maxLength={80}
            />
            <select
              className="ws-select"
              value={newKeyExpiry.label}
              onChange={e => setNewKeyExpiry(EXPIRY_OPTIONS.find(o => o.label === e.target.value) || EXPIRY_OPTIONS[0])}
            >
              {EXPIRY_OPTIONS.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
            </select>
            <button className="ws-btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating…' : 'Generate key'}
            </button>
          </div>
        )}

        {revealKey && (
          <div className="ws-reveal">
            <div className="ws-reveal-head">
              <span className="ws-reveal-icon">🔑</span>
              <span>Copy your key now — it won't be shown again.</span>
            </div>
            <div className="ws-reveal-key">
              <code>{revealKey.key}</code>
              <button className="ws-copy-btn" onClick={handleCopy}>{copied ? 'Copied ✓' : 'Copy'}</button>
            </div>
            <button className="ws-reveal-dismiss" onClick={() => setRevealKey(null)}>I've saved it</button>
          </div>
        )}

        {loadingKeys ? (
          <div className="ws-keys-empty">Loading keys…</div>
        ) : keys.length === 0 ? (
          <div className="ws-keys-empty">No API keys yet.{canManageKeys ? ' Generate one above to start using the REST API.' : ''}</div>
        ) : (
          <div className="ws-keys-table">
            <div className="ws-keys-row ws-keys-head">
              <span>Name</span>
              <span>Key</span>
              <span>Status</span>
              <span>Expires</span>
              <span>Calls</span>
              <span />
            </div>
            {keys.map(k => {
              const st = keyState(k);
              const isActive = st.label === 'Active';
              return (
                <div key={k.id} className="ws-keys-row">
                  <span className="ws-key-name">{k.name}</span>
                  <span className="ws-key-prefix"><code>{k.prefix}…</code></span>
                  <span><span className={`ws-key-badge ${st.cls}`}>{st.label}</span></span>
                  <span className="ws-key-exp">{k.expiresAt ? formatDate(k.expiresAt) : 'Never'}</span>
                  <span className="ws-key-calls">{k.callCount || 0}</span>
                  <span className="ws-key-actions">
                    {isActive && canManageKeys && (
                      <button className="ws-revoke-btn" onClick={() => handleRevoke(k)}>Revoke</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
